import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import {
    Building2,
    Home,
    Type,
    PanelBottom,
    Mail,
    HardHat,
    DollarSign,
    Zap,
    LogOut,
    Loader2,
    CheckCircle2,
    Save,
    Layout,
    MapPin,
    Mountain,
    Image as ImageIcon,
    Target,
    Plane,
    Truck,
    Info,
    Briefcase
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const AdminContact: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
    const [content, setContent] = useState<any>({});
    const [pendingImages, setPendingImages] = useState<{ [key: string]: { file: File, preview: string } }>({});
    const username = localStorage.getItem('adminUser') || 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
        } else {
            fetchContent();
        }
    }, [navigate]);

    const fetchContent = async () => {
        setLoading(true);
        try {
            const response = await fetch('/api/homepage/content');
            const data = await response.json();
            setContent(data);
        } catch (err) {
            console.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const saveSectionText = async (sectionName: string) => {
        setSaving(sectionName);
        const sectionData = content[sectionName] || {};

        try {
            const promises = Object.entries(sectionData)
                .filter(([key]) => !key.endsWith('_ar'))
                .map(([key, value]: [string, any]) => {
                    if (typeof value !== 'string') return Promise.resolve();
                    return fetch('/api/homepage/update-text', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                        },
                        body: JSON.stringify({ 
                            section: sectionName, 
                            content_key: key, 
                            content_value: value,
                            content_value_ar: sectionData[`${key}_ar`] || null
                        })
                    });
                });

            await Promise.all(promises);
        } catch (err) {
            console.error(`Save failed for ${sectionName}`);
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const handleImageSelect = (section: string, key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({
            ...prev,
            [`${section}-${key}`]: { file, preview }
        }));
    };

    const saveAllPendingImages = async () => {
        const totalPending = Object.keys(pendingImages).length;
        if (totalPending === 0) return;

        setSaving('all-images');
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const parts = key.split('-');
                const section = parts[0];
                const keyName = parts.slice(1).join('-');

                const formData = new FormData();
                formData.append('section', section);
                formData.append('content_key', keyName);
                formData.append('image', pending.file);

                const response = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    },
                    body: formData
                });

                if (response.ok) {
                    const data = await response.json();
                    return { section, keyName, imageUrl: data.imageUrl };
                } else {
                    const errorData = await response.json();
                    console.error(`Upload failed for ${section}-${keyName}:`, errorData);
                    return null;
                }
            });

            const results = await Promise.all(promises);

            setContent((prev: any) => {
                const next = { ...prev };
                results.forEach(res => {
                    if (res) {
                        next[res.section] = {
                            ...(next[res.section] || {}),
                            [res.keyName]: res.imageUrl
                        };
                    }
                });
                return next;
            });

            setPendingImages({});
        } catch (err) {
            console.error('Batch upload failed');
        } finally {
            setTimeout(() => setSaving(null), 1000);
        }
    };

    const navItems = [
        { id: 'dashboard', label: 'Homepage Editor', icon: Home, path: '/admin/dashboard' },
        { id: 'header', label: 'Header Editor', icon: Type, path: '/admin/header' },
        { id: 'footer', label: 'Footer Editor', icon: PanelBottom, path: '/admin/footer' },
        { id: 'contact', label: 'Contact Editor', icon: Mail, path: '/admin/contact' },
        { id: 'about', label: 'About Group Editor', icon: Info, path: '/admin/about' },
        { id: 'construction', label: 'Construction Editor', icon: HardHat, path: '/admin/construction' },
        { id: 'exchange', label: 'Exchange Editor', icon: DollarSign, path: '/admin/exchange' },
        { id: 'gabbro', label: 'Gabbro Editor', icon: Zap, path: '/admin/gabbro' },
        { id: 'mining', label: 'Mining Editor', icon: Mountain, path: '/admin/mining' },
        { id: 'plaza', label: 'Plaza Hotel Editor', icon: Building2, path: '/admin/plaza' },
        { id: 'realestate', label: 'Real Estate Editor', icon: MapPin, path: '/admin/realestate' },
        { id: 'tyres', label: 'Tyres Editor', icon: Target, path: '/admin/tyres' },
        { id: 'travel', label: 'Travel Editor', icon: Plane, path: '/admin/travel' },
        { id: 'transport', label: 'Transport Editor', icon: Truck, path: '/admin/transport' },
        { id: 'careers', label: 'Careers Editor', icon: Briefcase, path: '/admin/careers' },
    ];

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main Area */}
            <div className="flex-1 flex flex-col h-full bg-slate-50/50">
                {/* Fixed Header */}
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Mail className="w-6 h-6 text-blue-600" /> Contact Editor
                        </h2>

                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button
                                onClick={() => setActiveTab('content')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'content' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <Type className="w-4 h-4" />
                                <span>Content Details</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('images')}
                                className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'images' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                <ImageIcon className="w-4 h-4" />
                                <span>Media Assets</span>
                            </button>
                        </div>

                        {saving && (
                            <div className="flex items-center space-x-2 text-blue-600 font-bold text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span className="animate-pulse">Syncing...</span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-inner">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                {/* Scrollable Content Area */}
                <main className="flex-1 overflow-y-auto p-10 custom-scrollbar scroll-smooth">
                    <div className="max-w-4xl mx-auto">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-40">
                                <div className="w-16 h-16 border-4 border-blue-50 border-t-blue-600 rounded-full animate-spin mb-6" />
                                <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">Fetching Configuration...</p>
                            </div>
                        ) : (
                            <AnimatePresence mode="wait">
                                {activeTab === 'content' ? (
                                    <motion.div
                                        key="content-tab"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.4 }}
                                        className="space-y-8"
                                    >
                                        {/* Text Configuration */}
                                        <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm relative">
                                            <div className="flex items-center justify-between mb-8">
                                                <h3 className="text-lg font-bold text-slate-800 flex items-center">
                                                    <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                    Text Overrides
                                                </h3>
                                                <button
                                                    onClick={() => saveSectionText('Contact')}
                                                    disabled={saving === 'Contact'}
                                                    className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg shadow-emerald-200 active:scale-95"
                                                >
                                                    {saving === 'Contact' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                    <span>Save Contact Config</span>
                                                </button>
                                            </div>

                                            <div className="space-y-10">
                                                {/* Top Banner Tags */}
                                                <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
                                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                        <Layout className="w-4 h-4" /> Top Banner Settings
                                                    </h4>
                                                    <div className="space-y-6">
                                                        <BilingualInput 
                                                            label="Banner Small Feature Tag"
                                                            englishValue={content.Contact?.banner_tag}
                                                            arabicValue={content.Contact?.banner_tag_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), banner_tag: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), banner_tag_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Banner Title"
                                                            englishValue={content.Contact?.banner_title}
                                                            arabicValue={content.Contact?.banner_title_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), banner_title: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), banner_title_ar: val } })}
                                                        />
                                                        <BilingualTextArea 
                                                            label="Banner Subtitle / Desc"
                                                            englishValue={content.Contact?.banner_desc}
                                                            arabicValue={content.Contact?.banner_desc_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), banner_desc: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), banner_desc_ar: val } })}
                                                            rows={2}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Salalah Tab */}
                                                <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
                                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                        <MapPin className="w-4 h-4" /> Salalah Location Tab
                                                    </h4>
                                                    <div className="space-y-6">
                                                        <BilingualInput 
                                                            label="City / Title"
                                                            englishValue={content.Contact?.salalah_city}
                                                            arabicValue={content.Contact?.salalah_city_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_city: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_city_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="P.O. Box"
                                                            englishValue={content.Contact?.salalah_po}
                                                            arabicValue={content.Contact?.salalah_po_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_po: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_po_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Extra Detail"
                                                            englishValue={content.Contact?.salalah_extra}
                                                            arabicValue={content.Contact?.salalah_extra_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_extra: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_extra_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Postal Code"
                                                            englishValue={content.Contact?.salalah_postal}
                                                            arabicValue={content.Contact?.salalah_postal_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_postal: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_postal_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Telephone"
                                                            englishValue={content.Contact?.salalah_tel}
                                                            arabicValue={content.Contact?.salalah_tel_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_tel: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_tel_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Fax"
                                                            englishValue={content.Contact?.salalah_fax}
                                                            arabicValue={content.Contact?.salalah_fax_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_fax: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_fax_ar: val } })}
                                                        />
                                                        <div className="pt-4 border-t border-slate-100">
                                                            <label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Google Maps Embed URL (English)</label>
                                                            <input className="w-full mt-1.5 p-3.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-all"
                                                                value={content.Contact?.salalah_map || ''} placeholder="https://www.google.com/maps?q=Salalah%2C%20Oman&output=embed"
                                                                onChange={(e) => setContent({ ...content, Contact: { ...(content.Contact || {}), salalah_map: e.target.value } })} />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Muscat Tab */}
                                                <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
                                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                        <MapPin className="w-4 h-4" /> Muscat Location Tab
                                                    </h4>
                                                    <div className="space-y-6">
                                                        <BilingualInput 
                                                            label="City / Title"
                                                            englishValue={content.Contact?.muscat_city}
                                                            arabicValue={content.Contact?.muscat_city_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_city: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_city_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="P.O. Box"
                                                            englishValue={content.Contact?.muscat_po}
                                                            arabicValue={content.Contact?.muscat_po_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_po: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_po_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Extra Detail"
                                                            englishValue={content.Contact?.muscat_extra}
                                                            arabicValue={content.Contact?.muscat_extra_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_extra: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_extra_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Postal Code"
                                                            englishValue={content.Contact?.muscat_postal}
                                                            arabicValue={content.Contact?.muscat_postal_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_postal: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_postal_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Telephone"
                                                            englishValue={content.Contact?.muscat_tel}
                                                            arabicValue={content.Contact?.muscat_tel_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_tel: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_tel_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Fax"
                                                            englishValue={content.Contact?.muscat_fax}
                                                            arabicValue={content.Contact?.muscat_fax_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_fax: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_fax_ar: val } })}
                                                        />
                                                        <div className="pt-4 border-t border-slate-100">
                                                            <label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Google Maps Embed URL (English)</label>
                                                            <input className="w-full mt-1.5 p-3.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-all"
                                                                value={content.Contact?.muscat_map || ''} placeholder="https://www.google.com/maps?q=Muscat%2C%20Oman&output=embed"
                                                                onChange={(e) => setContent({ ...content, Contact: { ...(content.Contact || {}), muscat_map: e.target.value } })} />
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Bottom Description Form */}
                                                <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100">
                                                    <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                        <Mail className="w-4 h-4" /> Bottom Descriptive Content
                                                    </h4>
                                                    <div className="space-y-6">
                                                        <BilingualInput 
                                                            label="Main Header text"
                                                            englishValue={content.Contact?.bottom_title}
                                                            arabicValue={content.Contact?.bottom_title_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_title: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_title_ar: val } })}
                                                        />
                                                        <BilingualTextArea 
                                                            label="Description Paragraph"
                                                            englishValue={content.Contact?.bottom_desc}
                                                            arabicValue={content.Contact?.bottom_desc_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_desc: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_desc_ar: val } })}
                                                            rows={3}
                                                        />
                                                        <BilingualInput 
                                                            label="Contact Info Block - Address"
                                                            englishValue={content.Contact?.bottom_addr}
                                                            arabicValue={content.Contact?.bottom_addr_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_addr: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_addr_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Contact Info Block - Area"
                                                            englishValue={content.Contact?.bottom_area}
                                                            arabicValue={content.Contact?.bottom_area_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_area: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_area_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Contact Info Block - Telephone"
                                                            englishValue={content.Contact?.bottom_tel}
                                                            arabicValue={content.Contact?.bottom_tel_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_tel: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_tel_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Contact Info Block - Fax"
                                                            englishValue={content.Contact?.bottom_fax}
                                                            arabicValue={content.Contact?.bottom_fax_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_fax: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), bottom_fax_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Support Info Block - Telephone"
                                                            englishValue={content.Contact?.support_tel}
                                                            arabicValue={content.Contact?.support_tel_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), support_tel: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), support_tel_ar: val } })}
                                                        />
                                                        <BilingualInput 
                                                            label="Support Info Block - Email"
                                                            englishValue={content.Contact?.support_email}
                                                            arabicValue={content.Contact?.support_email_ar}
                                                            onChangeEnglish={val => setContent({ ...content, Contact: { ...(content.Contact || {}), support_email: val } })}
                                                            onChangeArabic={val => setContent({ ...content, Contact: { ...(content.Contact || {}), support_email_ar: val } })}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="images-tab"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.4 }}
                                        className="space-y-12"
                                    >
                                        <div className="flex items-center justify-between border-b pb-6">
                                            <div>
                                                <h3 className="text-2xl font-black text-slate-800 tracking-tight">Contact Media Library</h3>
                                                <p className="text-slate-500 text-sm font-medium">Manage all visual assets for the Contact Us page</p>
                                            </div>
                                            <button
                                                onClick={saveAllPendingImages}
                                                disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-blue-200 active:scale-95 disabled:bg-slate-300 flex items-center gap-3"
                                            >
                                                {saving === 'all-images' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                                                <span>Deploy Graphics</span>
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                            <div className="space-y-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                                                        <ImageIcon className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Primary Banner</h4>
                                                        <p className="text-[10px] text-slate-400 font-bold">Recommended: 1920x600px</p>
                                                    </div>
                                                </div>

                                                <div className="group bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col items-center">
                                                    <div className="relative w-full aspect-video bg-slate-50 rounded-2xl overflow-hidden mb-6 border border-slate-100 flex items-center justify-center">
                                                        {pendingImages['Contact-banner_image']?.preview ? (
                                                            <img src={pendingImages['Contact-banner_image'].preview} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Preview" />
                                                        ) : content.Contact?.banner_image ? (
                                                            <img src={content.Contact.banner_image} className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Saved Banner" />
                                                        ) : (
                                                            <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
                                                                <ImageIcon className="w-12 h-12 mb-2 opacity-20" />
                                                                <span className="text-xs font-bold uppercase tracking-widest">No Asset Loaded</span>
                                                            </div>
                                                        )}
                                                        <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                            <label className="p-4 bg-white rounded-2xl cursor-pointer hover:scale-110 transition-transform shadow-2xl">
                                                                <ImageIcon className="w-6 h-6 text-blue-600" />
                                                                <input type="file" className="hidden" accept="image/*" onChange={(e) => {
                                                                    if (e.target.files?.[0]) handleImageSelect('Contact', 'banner_image', e.target.files[0]);
                                                                }} />
                                                            </label>
                                                        </div>
                                                        {pendingImages['Contact-banner_image'] && (
                                                            <div className="absolute top-4 right-4 bg-emerald-500 text-white text-[10px] font-bold px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                                                                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                                                READY TO SAVE
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="space-y-4 w-full">
                                                        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                            <span>Asset Type</span>
                                                            <span>Contact_Banner_01</span>
                                                        </div>
                                                        <button
                                                            onClick={saveAllPendingImages}
                                                            disabled={!pendingImages['Contact-banner_image'] || saving === 'all-images'}
                                                            className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-100 disabled:text-slate-400 text-white rounded-2xl font-bold text-sm transition-all shadow-xl shadow-blue-100 flex items-center justify-center gap-3"
                                                        >
                                                            {saving === 'all-images' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                            {saving === 'all-images' ? 'Syncing...' : 'Upload & Deploy Slot'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center space-y-4">
                                                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mb-2">
                                                    <Mail className="w-8 h-8 text-blue-600" />
                                                </div>
                                                <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Section Maintenance</h4>
                                                <p className="text-xs text-slate-500 font-medium max-w-[240px]">
                                                    All maps in this section are currently integrated via Google Maps Embed URLs and can be managed in the Text Content tab.
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        )}
                        <div className="h-20" />
                    </div>
                </main>
            </div>


        </div>
    );
};

export default AdminContact;
